"use client";

import { Box, Heading, Separator, Stack, Text } from "@chakra-ui/react";
import { getHumanReadableTimestamp } from "@/shared/utils";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import LoadingState from "@/shared/ui/LoadingState";
import { CONTENT_ITEM_TEXT_PROPS } from "@/shared/ui/display/ContentItemPrimitives";
import useTraceOriginQuery from "../queries/useTraceOriginQuery";

type ThoughtTraceOriginProps = {
  originId?: string | null;
};

export default function ThoughtTraceOrigin({
  originId,
}: ThoughtTraceOriginProps) {
  const originQuery = useTraceOriginQuery(originId);
  const origin = originQuery.data ?? null;
  const processedStatus = origin?.processed_at
    ? "Source shaped"
    : "Source material";

  return (
    <Stack gap="4" align="stretch" width="full">
      {!originId ? (
        <GenericEmptyComponent
          title="No source attached"
          message="This confirmed thought was not shaped from stored source material."
          icon="origin_source"
          variant="inline"
        />
      ) : originQuery.isLoading ? (
        <LoadingState message="Loading source material..." />
      ) : originQuery.isError || !origin ? (
        <GenericEmptyComponent
          title="Source unavailable"
          message="The source material is no longer available for this thought."
          icon="not_found"
          variant="inline"
        />
      ) : (
        <Stack gap="3" align="stretch">
          <GenericDrawerInfo
            description="This is the source material that shaped the confirmed thought."
            items={[
              {
                label: processedStatus,
                colorPalette: origin.processed_at ? "green" : "gray",
              },
              ...(origin.language
                ? [
                    {
                      label: origin.language.toUpperCase(),
                      colorPalette: "teal" as const,
                    },
                  ]
                : []),
              {
                label: getHumanReadableTimestamp(origin.latest_at),
                colorPalette: "gray",
              },
            ]}
          />

          {origin.user_note ? (
            <Box
              borderWidth="1"
              borderColor="moveon.surface.border"
              rounded="md"
              p="3"
              bg="moveon.surface.elevatedBg"
            >
              <Stack gap="1" align="stretch">
                <Heading size="xs" color="moveon.surface.fg">
                  User context
                </Heading>
                <Text
                  fontSize="sm"
                  color="moveon.text.muted"
                  whiteSpace="pre-wrap"
                  overflowWrap="anywhere"
                  wordBreak="normal"
                  hyphens="auto"
                >
                  {origin.user_note}
                </Text>
              </Stack>
            </Box>
          ) : null}

          <Separator />

          <Box
            borderWidth="1"
            borderColor="moveon.surface.border"
            rounded="md"
            p="4"
            bg="moveon.surface.bg"
          >
            <Text color="moveon.surface.fg" {...CONTENT_ITEM_TEXT_PROPS}>
              {origin.raw_text_input}
            </Text>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
